import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const GlobalAIAssistant = () => {
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [agents, setUsers] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content: string }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]); // Array of proposed actions
  const [attachments, setAttachments] = useState([]); // { name, mediaType, data(base64), previewUrl }

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const loadedRef = useRef(false);

  const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv,.md,.csv';
  const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB per file

  // Load the persisted global thread when the panel opens (survives reload + follows
  // the user across tabs/devices). Reopening refreshes from the server source of truth.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/ai/conversation', { headers: { 'x-auth-token': token } });
        if (!cancelled) setMessages(res.data.messages || []);
      } catch (err) { console.error(err); }
      finally { loadedRef.current = true; }
    })();
    return () => { cancelled = true; };
  }, [isOpen, token]);

  // Persist the thread whenever it changes (debounced). Only metadata is stored server-side;
  // raw attachment bytes stay ephemeral.
  useEffect(() => {
    if (!loadedRef.current) return;
    const t = setTimeout(() => {
      axios.put('/api/ai/conversation', {
        messages: messages.map(m => ({
          role: m.role,
          content: m.content,
          attachments: (m.attachments || []).map(a => ({ name: a.name, mediaType: a.mediaType, isImage: !!(a.isImage || a.previewUrl) })),
        })),
      }, { headers: { 'x-auth-token': token } }).catch(err => console.error('persist chat failed', err));
    }, 600);
    return () => clearTimeout(t);
  }, [messages, token]);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const fetchAgents = async () => {
    try {
      const res = await axios.get('/api/users', { headers: { 'x-auth-token': token } });
      const filteredAgents = res.data.filter(u => u.isAgent && u.isLocalAgent);
      setUsers(filteredAgents);
      if (filteredAgents.length > 0 && !selectedAgentId) {
        setSelectedAgentId(filteredAgents[0]._id);
      }
    } catch (err) { console.error(err); }
  };

  // Read selected files into base64 attachment objects (data URL kept for preview).
  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    files.forEach(file => {
      if (file.size > MAX_FILE_BYTES) {
        alert(`"${file.name}" is larger than 10MB and was skipped.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments(prev => [...prev, {
          name: file.name,
          mediaType: file.type || 'application/octet-stream',
          data: reader.result, // data URL; backend strips the prefix
          previewUrl: (file.type || '').startsWith('image/') ? reader.result : null,
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleNewChat = async () => {
    setMessages([]);
    setProposals([]);
    setAttachments([]);
    try {
      await axios.delete('/api/ai/conversation', { headers: { 'x-auth-token': token } });
    } catch (err) { console.error('clear chat failed', err); }
  };

  // Auto-grow the message box with content, up to ~5 lines (also shrinks back after send).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input, isOpen, proposals.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!input.trim() && attachments.length === 0) || !selectedAgentId) return;

    const userMessage = input.trim() || (attachments.length ? '(see attached file)' : '');
    const sentAttachments = attachments;
    setMessages(prev => [...prev, { role: 'user', content: userMessage, attachments: sentAttachments }]);
    setInput('');
    setAttachments([]);
    setLoading(true);
    setProposals([]);

    try {
      const currentBoardId = location.pathname.startsWith('/boards/') ? location.pathname.split('/').pop() : null;

      const res = await axios.post('/api/ai/chat', {
        message: userMessage,
        agentId: selectedAgentId,
        history: messages,
        currentBoardId,
        attachments: sentAttachments.map(a => ({ name: a.name, mediaType: a.mediaType, data: a.data })),
      }, { headers: { 'x-auth-token': token } });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.text }]);
      
      if (res.data.type === 'proposal' && res.data.proposals) {
        setProposals(res.data.proposals);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.msg || err.response?.data || err.message || "Unknown error";
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ Error: ${errorMsg}. Please check your AI configuration in the Admin Brand settings.` 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await axios.post('/api/ai/execute', { proposals, agentId: selectedAgentId }, { headers: { 'x-auth-token': token } });
      setMessages(prev => [...prev, { role: 'assistant', content: `✅ Done! I've executed ${proposals.length} actions for you.` }]);
      setProposals([]);
      // The board live-updates via Socket.io (executeAction emits card events), so no reload needed.
    } catch (err) {
      const errorMsg = err.response?.data?.msg || err.message;
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ Failed to execute the action: ${errorMsg}` }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-purple-600 text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-purple-700 transition-all hover:scale-110 z-40 animate-pulse"
        title="AI Assistant"
      >
        <span className="text-2xl">🤖</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-[30rem] max-w-[calc(100vw-3rem)] max-h-[600px] h-[80vh] bg-white dark:bg-gray-800 rounded-3xl shadow-2xl z-50 flex flex-col overflow-hidden border border-purple-100 dark:border-purple-900 animate-fade-in">
      {/* Header */}
      <div className="p-4 bg-purple-600 text-white flex justify-between items-center">
        <div className="flex items-center">
          <span className="text-xl mr-2">🤖</span>
          <div>
            <h3 className="text-sm font-black uppercase tracking-tighter leading-none">Local AI Assistant</h3>
            <p className="text-[10px] opacity-80 font-bold uppercase mt-1 tracking-widest">Active & Ready</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={handleNewChat} title="New chat (clears history)" className="hover:bg-white/20 p-1 rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
          </button>
          <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
      </div>

      {/* Agent Selector */}
      <div className="p-2 bg-purple-50 dark:bg-gray-700 border-b border-purple-100 dark:border-purple-900">
        {agents.length > 0 ? (
          <select
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            className="w-full text-[10px] font-black uppercase bg-transparent border-none focus:ring-0 text-purple-700 dark:text-purple-400 cursor-pointer"
          >
            {agents.map(a => (
              <option key={a._id} value={a._id}>Speaking with: {a.name}</option>
            ))}
          </select>
        ) : (
          <div className="text-[10px] font-black text-red-500 uppercase px-2 py-1">
            ⚠️ No Local Agents Found. Create one in Admin.
          </div>
        )}
      </div>

      {/* Chat Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-purple-200 dark:scrollbar-thumb-purple-700"
      >
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="text-4xl mb-4 opacity-20">✨</div>
            <p className="text-xs text-gray-400 dark:text-gray-400 font-bold uppercase tracking-widest leading-relaxed">
              Tell me what you'd like to do.<br/>
              I can create or delete cards across any board.
            </p>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-3 rounded-2xl text-xs font-medium leading-relaxed shadow-sm ${
              m.role === 'user'
                ? 'bg-purple-600 text-white rounded-tr-none'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-tl-none border border-gray-200 dark:border-gray-600'
            }`}>
              {m.attachments && m.attachments.length > 0 && (
                <div className="mb-2 space-y-1">
                  {m.attachments.map((a, ai) => (
                    a.previewUrl ? (
                      <img key={ai} src={a.previewUrl} alt={a.name} className="max-w-full max-h-40 rounded-lg border border-white/20" />
                    ) : (
                      <div key={ai} className="flex items-center gap-1 text-[10px] font-bold opacity-90 bg-black/10 dark:bg-white/10 rounded px-2 py-1">
                        <span>📎</span><span className="truncate">{a.name}</span>
                      </div>
                    )
                  ))}
                </div>
              )}
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-2xl rounded-tl-none border border-gray-200 dark:border-gray-600 shadow-sm animate-pulse">
              <div className="flex space-x-1">
                <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:0.4s]"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Proposals Action Confirmation */}
      {proposals.length > 0 && (
        <div className="p-4 bg-purple-50 dark:bg-gray-700 border-t border-purple-100 dark:border-purple-900 animate-fade-in">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-purple-200 dark:border-purple-900 shadow-sm mb-3 max-h-40 overflow-y-auto scrollbar-thin">
            <p className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase mb-2 tracking-widest">Proposed Actions ({proposals.length}):</p>
            <div className="space-y-2">
              {proposals.map((p, i) => (
                <div key={i} className={`text-[10px] border-l-2 pl-2 py-1 ${p.action === 'delete_card' ? 'border-red-400' : 'border-purple-200 dark:border-purple-600'}`}>
                  {p.action === 'create_card' && (
                    <p className="font-bold text-gray-800 dark:text-gray-200">✨ Create: "{p.data.title}"</p>
                  )}
                  {p.action === 'delete_card' && (
                    <p className="font-bold text-red-600 dark:text-red-400">🗑️ Delete: "{p.data.title || p.data.cardId}"</p>
                  )}
                  {p.action === 'comment_card' && (
                    <p className="font-bold text-gray-800 dark:text-gray-200">💬 Comment on card ID: {p.data.cardId?.substring(0, 8)}...</p>
                  )}
                  {p.action === 'create_mindmap' && (
                    <div>
                      <p className="font-bold text-gray-800 dark:text-gray-200">🧠 Create mind map: "{p.data.name}"</p>
                      {p.data.tree?.children && (
                        <ul className="mt-1 ml-2 text-gray-500 dark:text-gray-400">
                          {p.data.tree.children.slice(0, 6).map((c, j) => (
                            <li key={j}>• {c.text}{c.children?.length ? ` (+${c.children.length})` : ''}</li>
                          ))}
                          {p.data.tree.children.length > 6 && <li>…and {p.data.tree.children.length - 6} more branches</li>}
                        </ul>
                      )}
                    </div>
                  )}
                  {p.action === 'add_mindmap_nodes' && (
                    <div>
                      <p className="font-bold text-gray-800 dark:text-gray-200">🧠 Expand mind map branch</p>
                      <ul className="mt-1 ml-2 text-gray-500 dark:text-gray-400">
                        {(Array.isArray(p.data.tree) ? p.data.tree : [p.data.tree]).slice(0, 6).map((c, j) => (
                          <li key={j}>• {c?.text}{c?.children?.length ? ` (+${c.children.length})` : ''}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {p.action === 'update_mindmap_node' && (
                    <p className="font-bold text-gray-800 dark:text-gray-200">🧠 Update topic: "{p.data.text || p.data.nodeId}"</p>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleConfirm}
              className={`flex-1 text-white py-2 rounded-lg text-xs font-black uppercase tracking-widest shadow-lg transition-all ${proposals.some(p => p.action === 'delete_card') ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              Confirm All ({proposals.length})
            </button>
            <button 
              onClick={() => setProposals([])}
              className="px-4 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 py-2 rounded-lg text-xs font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      {proposals.length === 0 && (
        <form onSubmit={handleSend} className="p-4 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
          {/* Attachment preview chips */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {attachments.map((a, i) => (
                <div key={i} className="relative group">
                  {a.previewUrl ? (
                    <img src={a.previewUrl} alt={a.name} className="w-12 h-12 object-cover rounded-lg border border-gray-200 dark:border-gray-600" />
                  ) : (
                    <div className="flex items-center gap-1 max-w-[140px] bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 text-[10px] font-bold text-gray-700 dark:text-gray-200">
                      <span>📎</span><span className="truncate">{a.name}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-700 text-white rounded-full text-[10px] leading-none flex items-center justify-center hover:bg-red-600"
                    title="Remove"
                  >×</button>
                </div>
              ))}
            </div>
          )}
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            />
            <textarea
              ref={inputRef}
              rows={1}
              placeholder="Message AI..."
              className="block w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl py-3 pl-10 pr-12 text-xs font-bold text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-purple-500 focus:border-purple-500 resize-none overflow-y-auto max-h-[120px] leading-relaxed scrollbar-thin"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={loading}
              title="Attach image or file"
              className="absolute left-2 bottom-2.5 p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg disabled:opacity-30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path></svg>
            </button>
            <button
              type="submit"
              disabled={loading || (!input.trim() && attachments.length === 0)}
              className="absolute right-2 bottom-2 p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg disabled:opacity-30"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"></path></svg>
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default GlobalAIAssistant;
